// NJM Build Application v2 (2026-02) — data only, matches the approved HTML prototype +
// the Application Form Blueprint exactly. Every option list is fetched live from
// /v1/build/apply/field-options (mirrored straight off the Notion schema Claude staged) —
// this file only defines STRUCTURE (which questions, in what order, for which role), never
// hardcodes an option value itself, so a founder editing an option list in Notion later
// needs zero code changes here.

// Canonical role mapping (2026-02, unblocking the P1 item): the picker's SELECTABLE roles now
// come live from `/v1/build/apply/field-options`'s `primary_role`/`secondary_roles` (sourced
// from the Role Directory, Active=true — the exact same source the main freelancer pickers
// already use, per build.py's `_canonical_role_names()`) — never a hardcoded copy here, that
// duplication is exactly what caused the original role drift the founder called out.
// `ROLE_META` below is ONLY cosmetic display metadata (a short tag + one-line description for
// the picker cards) for the roles we have copy for; BuildApplyV2.jsx falls back to a generic
// tag/description for any canonical role not listed here (e.g. a role added later in Notion
// still renders, just without custom copy — never hidden).
// Renames applied to match the canonical Directory's exact names (founder-confirmed 2026-02):
// Video Editor -> Editor, DI-Colorist -> Colorist, FPV/Drone Pilot -> Drone Operator,
// Copywriting-Corporate -> Copywriter. "Graphics Design" folded into "VFX Artist" and
// "360 Content Creator" folded into "Creator" (both keep their original specialised
// questions, just filed under the canonical name — founder's explicit instruction).
export const ROLE_META = {
  Cinematographer: { tag: "CI", description: "Camera, lighting, on-set direction of footage" },
  Photographer: { tag: "PH", description: "Stills: event, corporate, fashion, real estate" },
  Editor: { tag: "VE", description: "Post-production cut, pacing, sound design" },
  "Creative Director": { tag: "CD", description: "Pre-production, shot breakdown, on-set feedback" },
  "VFX Artist": { tag: "GD", description: "Motion VFX, logo animation, template creation" },
  Colorist: { tag: "DI", description: "Colour grading and finishing" },
  "Line Producer": { tag: "LP", description: "Crewing, logistics, on-ground production" },
  "Drone Operator": { tag: "DR", description: "Aerial cinematography and FPV" },
  Creator: { tag: "360", description: "360/VR capture, editing, installations" },
  "Voice Over Artist": { tag: "VO", description: "Voice recording in one or more languages" },
  "AI Creator": { tag: "AI", description: "AI-hybrid prompt engineering and generation" },
  Copywriter: { tag: "CW", description: "Scripts, decks, subtitling, translation" },
  Gaffer: { tag: "GF", description: "Lighting setup and on-set electrical" },
  "Sound Recordist": { tag: "SR", description: "On-set audio capture and boom/lav operation" },
  Other: { tag: "OT", description: "Something not listed above" },
};

export const YEARS_OPTIONS_KEY = "experience_level";
// Representative numeric value written to the existing "Experience (Years)" Number field
// alongside the new Experience Level select — per the founder's explicit instruction #2:
// keep anything that already reads the Number field working for new applicants too, without
// migrating existing members' numbers.
export const YEARS_TO_NUMBER = {
  "Under 1 year": 0, "2-3 years": 2, "4-5 years": 4, "5-10 years": 5, "Over 10 years": 10,
  "Under 1 year ": 0,
};

export const PROJECT_TYPES_KEY = "preferred_project_types";
export const PROJECT_TYPES_MAX = 2;
export const HOW_HEARD_KEY = "how_heard";
export const WORK_MODEL_KEY = "work_model";

// One entry per question. `optionsKey` indexes the /apply/field-options response.
// kind: "multi" | "single" | "text". `optional: true` fields can stay empty (blueprint's
// "optional, quick-tick Gear & Software" note); everything else in a branch is required.
//
// Freelancer Edit Profile Simplification (2026-09): moved out of this file into
// `@/constants/roleBranches.json` — the backend (build.py) needs the EXACT same
// role -> sub-option mapping to validate a freelancer's self-edit (reject a niche/service
// that doesn't belong to their current role), and a Python module can't import a .js file.
// This is now the ONE shared source of truth both sides read; edit the JSON, not here.
import roleBranchesData from "@/constants/roleBranches.json";

export const BRANCHES = roleBranchesData.BRANCHES;

export const LINK_FIELDS = [
  ["work_links", "Work links (reel, portfolio, samples)"],
  ["website", "Website"],
  ["youtube", "YouTube"],
  ["vimeo", "Vimeo"],
  ["behance", "Behance"],
];

export const SAVE_KEY = "njm_build_apply_v2";

export const EMPTY_FORM_V2 = {
  name: "", registered_gmail: "", phone: "", instagram: "", city: "", country: "",
  experience_level: "", work_model: "", how_heard: "", referral_contact: "", achievements: "", bio: "",
  preferred_project_types: [],
  primary_role: "", secondary_roles: [],
  branch: {},
  work_links: "", website: "", youtube: "", vimeo: "", behance: "",
};
